import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
  Navigate,
} from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import AuthCallback from "./components/AuthCallback";
import { SiteHead } from "./components/Seo";
import { Loader2 } from "lucide-react";
import { FeatureGate } from "./context/SocietyFeatureContext";

import Landing from "./pages/PremiumLanding";
import Login from "./pages/Login";
import Onboarding from "./pages/Onboarding";
import KnowledgePublic from "./pages/KnowledgePublic";
import KnowledgeArticle from "./pages/KnowledgeArticle";

import PortalLayout from "./components/PortalLayout";
import Dashboard from "./pages/portal/Dashboard";
import Members from "./pages/portal/Members";
import Tools from "./pages/portal/Tools";
import SocietyAccounting from "./pages/portal/SocietyAccounting";
import AssetsInventory from "./pages/portal/AssetsInventory";
import SocietyAmc from "./pages/portal/SocietyAmc";
import Requirements from "./pages/portal/Requirements";
import Projects from "./pages/portal/Projects";
import Complaints from "./pages/portal/Complaints";
import Helpdesk from "./pages/portal/Helpdesk";
import Documents from "./pages/portal/Documents";
import AskMitra from "./pages/portal/AskMitra";
import Experts from "./pages/portal/Experts";
import SocietySettings from "./pages/portal/SocietySettings";
import SocietyIntegrations from "./pages/portal/SocietyIntegrations";
import SocietySetup from "./pages/portal/SocietySetup";
import SocietyDirectory from "./pages/portal/SocietyDirectory";
import SocietyOnboardingImport from "./pages/portal/SocietyOnboardingImport";
import SocietyFinance from "./pages/portal/SocietyFinance";
import FreshFinancePreview from "./pages/portal/FreshFinancePreview";
import SocietyStaff from "./pages/portal/SocietyStaff";
import SocietyLetterheadPage from "./pages/portal/SocietyLetterheadPage";

import AdminLayout from "./components/AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminSocieties from "./pages/admin/AdminSocieties";
import AdminSocietyOverview from "./pages/admin/AdminSocietyOverview";
import AdminLeads from "./pages/admin/AdminLeads";
import AdminVendors from "./pages/admin/AdminVendors";
import AdminRequirements from "./pages/admin/AdminRequirements";
import AdminAudit from "./pages/admin/AdminAudit";
import AdminKnowledge from "./pages/admin/AdminKnowledge";
import AdminMonetization from "./pages/admin/AdminMonetization";
import AdminSubscriptions from "./pages/admin/AdminSubscriptions";
import AdminRevenue from "./pages/admin/AdminRevenue";
import AdminExperts from "./pages/admin/AdminExperts";
import AdminExpertRequests from "./pages/admin/AdminExpertRequests";
import AdminExpertAppointments from "./pages/admin/AdminExpertAppointments";
import AdminExpertSessions from "./pages/admin/AdminExpertSessions";
import AdminExpertCommercial from "./pages/admin/AdminExpertCommercial";
import AdminExpert360 from "./pages/admin/AdminExpert360";
import AdminApplications from "./pages/admin/AdminApplications";
import AdminProviderApplications from "./pages/admin/AdminProviderApplications";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminCrmDashboard from "./pages/admin/AdminCrmDashboard";
import AdminStaff from "./pages/admin/AdminStaff";
import AdminDepartments from "./pages/admin/AdminDepartments";
import AdminRoles from "./pages/admin/AdminRoles";
import AdminServices from "./pages/admin/AdminServices";
import AdminResidentServices from "./pages/admin/AdminResidentServices";
import AdminVendorLegal from "./pages/admin/AdminVendorLegal";
import AdminVendorPilots from "./pages/admin/AdminVendorPilots";
import AdminPaymentSettings from "./pages/admin/AdminPaymentSettings";
import AdminPilotBookings from "./pages/admin/AdminPilotBookings";
import AdminCompanyFinance from "./pages/admin/AdminCompanyFinance";
import AdminCommercial from "./pages/admin/AdminCommercial";
import AdminCommercialQuotes from "./pages/admin/AdminCommercialQuotes";
import AdminReports from "./pages/admin/AdminReports";
import AdminFeatures from "./pages/admin/AdminFeatures";

import VendorPortal from "./pages/vendor/VendorPortal";
import VendorHome from "./pages/vendor/VendorHome";
import VendorProfile from "./pages/vendor/VendorProfile";
import VendorServices from "./pages/vendor/VendorServices";
import VendorServiceAreas from "./pages/vendor/VendorServiceAreas";
import VendorDocuments from "./pages/vendor/VendorDocuments";
import VendorSubscription from "./pages/vendor/VendorSubscription";
import VendorActivation from "./pages/vendor/VendorActivation";
import VendorPending from "./pages/vendor/VendorPending";
import VendorPilotBookings from "./pages/vendor/VendorPilotBookings";
import ExpertPortal from "./pages/expert/ExpertPortal";
import ExpertFoundation from "./pages/expert/ExpertFoundation";
import ExpertActivation from "./pages/expert/ExpertActivation";
import ExpertPending from "./pages/expert/ExpertPending";
import Notices from "./pages/portal/Notices";
import ResidentLayout from "./components/ResidentLayout";
import ResidentHome from "./pages/resident/ResidentHome";
import ResidentHomeModern from "./pages/resident/ResidentHomeModern";
import ResidentMySociety from "./pages/resident/ResidentMySociety";
import ResidentMyUnits from "./pages/resident/ResidentMyUnits";
import ResidentProfile from "./pages/resident/ResidentProfile";
import ResidentMyHousehold from "./pages/resident/ResidentMyHousehold";
import ResidentUnitDetail from "./pages/resident/ResidentUnitDetail";
import ResidentActivation from "./pages/resident/ResidentActivation";
import ResidentAccess from "./pages/portal/ResidentAccess";
import ResidentFinance from "./pages/resident/ResidentFinance";
import ResidentFinanceDetail from "./pages/resident/ResidentFinanceDetail";
import ResidentLedger from "./pages/resident/ResidentLedger";
import ResidentHelpdesk from "./pages/resident/ResidentHelpdesk";
import ResidentTechnicianServices from "./pages/resident/ResidentTechnicianServices";
import ResidentDoorstepServices from "./pages/resident/ResidentDoorstepServices";
import ResidentTicketDetail from "./pages/resident/ResidentTicketDetail";
import ResidentDocuments from "./pages/resident/ResidentDocuments";
import ResidentNoticeDetail from "./pages/resident/ResidentNoticeDetail";
import ResidentNotices from "./pages/resident/ResidentNotices";
import ResidentCommunications from "./pages/portal/ResidentCommunications";
import SocietyMeetings from "./pages/portal/SocietyMeetings";
import ResidentMeetings from "./pages/resident/ResidentMeetings";
import ResidentMeetingDetail from "./pages/resident/ResidentMeetingDetail";
import ResidentFormalRequests from "./pages/resident/ResidentFormalRequests";
import ResidentFormalRequestDetail from "./pages/resident/ResidentFormalRequestDetail";
import SocietyFormalRequests from "./pages/portal/SocietyFormalRequests";
import GateSecurity from "./pages/portal/GateSecurity";
import ResidentVisitorApprovals from "./pages/resident/ResidentVisitorApprovals";
import SocietyAmenities from "./pages/portal/SocietyAmenities";
import ResidentAmenities from "./pages/resident/ResidentAmenities";
import SocietyParking from "./pages/portal/SocietyParking";
import ResidentParking from "./pages/resident/ResidentParking";
import PublicBillPayment from "./pages/PublicBillPayment";
import ProviderApplication from "./pages/ProviderApplication";

function Protected({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (!user.onboarded || !user.role)
    return <Navigate to="/onboarding" replace />;
  if (roles && !roles.includes(user.role)) {
    if (user.role === "super_admin" || user.role === "staff")
      return <Navigate to="/admin" replace />;
    if (user.role === "vendor") return <Navigate to="/vendor" replace />;
    if (user.role === "expert") return <Navigate to="/expert" replace />;
    if (user.role === "society_member")
      return <Navigate to="/resident" replace />;
    return <Navigate to="/app" replace />;
  }
  return children;
}

function ResidentProtected({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return <div className="min-h-screen" data-testid="resident-auth-loading" />;
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AdminModule({ children, module }) {
  const { user } = useAuth();
  if (user?.role === "super_admin" || user?.permissions?.[module]?.view)
    return children;
  return (
    <div
      className="min-h-[50vh] flex items-center justify-center text-center"
      data-testid="admin-module-denied"
    >
      <div>
        <h1 className="font-display text-2xl font-bold">Module not assigned</h1>
        <p className="text-sm text-muted-foreground mt-2">
          Your staff role does not include access to this module.
        </p>
      </div>
    </div>
  );
}

const SOCIETY_ROLES = ["society_admin", "accountant", "society_portal"];
const ADMIN = ["super_admin", "staff"];

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/knowledge" element={<KnowledgePublic />} />
      <Route path="/knowledge/:slug" element={<KnowledgeArticle />} />
      <Route path="/onboarding" element={<Onboarding />} />
      <Route path="/join-provider" element={<ProviderApplication />} />
      <Route path="/pay/bill/:token" element={<PublicBillPayment />} />

      <Route
        path="/app"
        element={
          <Protected roles={SOCIETY_ROLES}>
            <PortalLayout />
          </Protected>
        }
      >
        <Route index element={<Dashboard />} />
        <Route
          path="members"
          element={
            <FeatureGate featureKey="sm02_directory">
              <Members />
            </FeatureGate>
          }
        />
        <Route path="tools" element={<Tools />} />
        <Route path="letterhead" element={<SocietyLetterheadPage />} />
        <Route
          path="accounting"
          element={
            <FeatureGate featureKey="sm04_accounting">
              <SocietyAccounting />
            </FeatureGate>
          }
        />
        <Route
          path="assets"
          element={
            <FeatureGate featureKey="sm06_assets_inventory">
              <AssetsInventory />
            </FeatureGate>
          }
        />
        <Route
          path="amc"
          element={
            <FeatureGate featureKey="sm07_amc_contracts">
              <SocietyAmc />
            </FeatureGate>
          }
        />
        <Route path="requirements" element={<Requirements />} />
        <Route path="projects" element={<Projects />} />
        <Route
          path="complaints"
          element={
            <FeatureGate featureKey="sm05_helpdesk">
              <Helpdesk />
            </FeatureGate>
          }
        />
        <Route path="documents" element={<Documents />} />
        <Route path="notices" element={<ResidentCommunications />} />
        <Route
          path="meetings"
          element={
            <FeatureGate featureKey="rp06_meetings">
              <SocietyMeetings />
            </FeatureGate>
          }
        />
        <Route
          path="requests"
          element={
            <FeatureGate featureKey="rp07_formal_requests">
              <SocietyFormalRequests />
            </FeatureGate>
          }
        />
        <Route
          path="gate-security"
          element={
            <FeatureGate featureKey="rp08_gate_security">
              <GateSecurity />
            </FeatureGate>
          }
        />
        <Route path="ask" element={<AskMitra />} />
        <Route path="experts" element={<Experts />} />
        <Route path="settings" element={<SocietySettings />} />
        <Route path="integrations" element={<SocietyIntegrations />} />
        <Route path="setup" element={<SocietySetup />} />
        <Route path="import-old-data" element={<SocietyOnboardingImport />} />
        <Route
          path="directory"
          element={
            <FeatureGate featureKey="sm02_directory">
              <SocietyDirectory />
            </FeatureGate>
          }
        />
        <Route
          path="finance"
          element={
            <FeatureGate featureKey="sm03_billing">
              <SocietyFinance />
            </FeatureGate>
          }
        />
        <Route path="finance-preview" element={<FreshFinancePreview />} />
        <Route
          path="staff"
          element={
            <FeatureGate featureKey="sm08_staff_workforce">
              <SocietyStaff />
            </FeatureGate>
          }
        />
        <Route path="resident-access" element={<ResidentAccess />} />
        <Route
          path="amenities"
          element={
            <FeatureGate featureKey="rp09_amenity_booking">
              <SocietyAmenities />
            </FeatureGate>
          }
        />
        <Route
          path="parking"
          element={
            <FeatureGate featureKey="rp10_parking_vehicles">
              <SocietyParking />
            </FeatureGate>
          }
        />
      </Route>

      <Route
        path="/admin"
        element={
          <Protected roles={ADMIN}>
            <AdminLayout />
          </Protected>
        }
      >
        <Route
          index
          element={
            <AdminModule module="management_dashboard">
              <AdminDashboard />
            </AdminModule>
          }
        />
        <Route
          path="reports"
          element={
            <AdminModule module="management_reports">
              <AdminReports />
            </AdminModule>
          }
        />
        <Route
          path="societies"
          element={
            <AdminModule module="societies">
              <AdminSocieties />
            </AdminModule>
          }
        />
        <Route
          path="societies/:societyId/setup"
          element={
            <AdminModule module="societies">
              <AdminSocietyOverview />
            </AdminModule>
          }
        />
        <Route
          path="features"
          element={
            <AdminModule module="feature_registry">
              <AdminFeatures />
            </AdminModule>
          }
        />
        <Route
          path="features/:societyId"
          element={
            <AdminModule module="feature_registry">
              <AdminFeatures />
            </AdminModule>
          }
        />
        <Route path="finance-preview" element={<FreshFinancePreview adminMode />} />
        <Route
          path="users"
          element={
            <AdminModule module="users_access">
              <AdminUsers />
            </AdminModule>
          }
        />
        <Route
          path="crm"
          element={
            <AdminModule module="crm_leads">
              <AdminCrmDashboard />
            </AdminModule>
          }
        />
        <Route
          path="staff"
          element={
            <AdminModule module="staff">
              <AdminStaff />
            </AdminModule>
          }
        />
        <Route
          path="departments"
          element={
            <AdminModule module="departments">
              <AdminDepartments />
            </AdminModule>
          }
        />
        <Route
          path="roles"
          element={
            <AdminModule module="roles">
              <AdminRoles />
            </AdminModule>
          }
        />
        <Route
          path="leads"
          element={
            <AdminModule module="crm_leads">
              <AdminLeads />
            </AdminModule>
          }
        />
        <Route
          path="vendors"
          element={
            <AdminModule module="vendors">
              <AdminVendors />
            </AdminModule>
          }
        />
        <Route
          path="requirements"
          element={
            <AdminModule module="requirements">
              <AdminRequirements />
            </AdminModule>
          }
        />
        <Route
          path="services"
          element={
            <AdminModule module="services">
              <AdminServices />
            </AdminModule>
          }
        />
        <Route
          path="resident-services"
          element={
            <AdminModule module="services">
              <AdminResidentServices />
            </AdminModule>
          }
        />
        <Route
          path="vendor-legal"
          element={
            <AdminModule module="vendors">
              <AdminVendorLegal />
            </AdminModule>
          }
        />
        <Route
          path="vendor-pilots"
          element={
            <AdminModule module="vendors">
              <AdminVendorPilots />
            </AdminModule>
          }
        />
        <Route
          path="payment-settings"
          element={
            <AdminModule module="vendors">
              <AdminPaymentSettings />
            </AdminModule>
          }
        />
        <Route
          path="pilot-bookings"
          element={
            <AdminModule module="vendors">
              <AdminPilotBookings />
            </AdminModule>
          }
        />
        <Route
          path="company-finance"
          element={
            <AdminModule module="company_accounting">
              <AdminCompanyFinance />
            </AdminModule>
          }
        />
        <Route
          path="commercial"
          element={
            <AdminModule module="commercial_dashboard">
              <AdminCommercial />
            </AdminModule>
          }
        />
        <Route
          path="commercial-quotes"
          element={
            <AdminModule module="commercial_quotes">
              <AdminCommercialQuotes />
            </AdminModule>
          }
        />
        <Route
          path="knowledge"
          element={
            <AdminModule module="knowledge">
              <AdminKnowledge />
            </AdminModule>
          }
        />
        <Route
          path="monetization"
          element={
            <AdminModule module="monetization">
              <AdminMonetization />
            </AdminModule>
          }
        />
        <Route
          path="subscriptions"
          element={
            <AdminModule module="subscriptions">
              <AdminSubscriptions />
            </AdminModule>
          }
        />
        <Route
          path="revenue"
          element={
            <AdminModule module="revenue">
              <AdminRevenue />
            </AdminModule>
          }
        />
        <Route
          path="experts"
          element={
            <AdminModule module="experts">
              <AdminExpert360 />
            </AdminModule>
          }
        />
        <Route
          path="consultation-requests"
          element={
            <AdminModule module="experts">
              <AdminExpertRequests />
            </AdminModule>
          }
        />
        <Route
          path="appointments"
          element={
            <AdminModule module="experts">
              <AdminExpertAppointments />
            </AdminModule>
          }
        />
        <Route
          path="consultation-sessions"
          element={
            <AdminModule module="experts">
              <AdminExpertSessions />
            </AdminModule>
          }
        />
        <Route
          path="expert-commercial"
          element={
            <AdminModule module="commercial_dashboard">
              <AdminExpertCommercial />
            </AdminModule>
          }
        />
        <Route
          path="applications"
          element={
            <AdminModule module="applications">
              <AdminApplications />
            </AdminModule>
          }
        />
        <Route
          path="provider-applications"
          element={
            <AdminModule module="applications">
              <AdminProviderApplications />
            </AdminModule>
          }
        />
        <Route
          path="audit"
          element={
            <AdminModule module="audit_logs">
              <AdminAudit />
            </AdminModule>
          }
        />
      </Route>

      <Route
        path="/vendor"
        element={
          <Protected roles={["vendor"]}>
            <VendorPortal />
          </Protected>
        }
      >
        <Route index element={<VendorHome />} />
        <Route path="profile" element={<VendorProfile />} />
        <Route path="services" element={<VendorServices />} />
        <Route path="service-areas" element={<VendorServiceAreas />} />
        <Route path="documents" element={<VendorDocuments />} />
        <Route path="subscription" element={<VendorSubscription />} />
        <Route path="pilot-bookings" element={<VendorPilotBookings />} />
      </Route>
      <Route path="/vendor/activate" element={<VendorActivation />} />
      <Route path="/vendor/pending" element={<VendorPending />} />
      <Route
        path="/expert"
        element={
          <Protected roles={["expert"]}>
            <ExpertFoundation />
          </Protected>
        }
      />
      <Route path="/expert/activate" element={<ExpertActivation />} />
      <Route path="/expert/pending" element={<ExpertPending />} />
      <Route path="/resident/activate" element={<ResidentActivation />} />
      <Route path="/app/resident/activate" element={<ResidentActivation />} />
      <Route
        path="/resident"
        element={
          <ResidentProtected>
            <ResidentLayout />
          </ResidentProtected>
        }
      >
        <Route index element={<ResidentHomeModern />} />
        <Route path="society" element={<ResidentMySociety />} />
        <Route path="units" element={<ResidentMyUnits />} />
        <Route path="unit" element={<ResidentUnitDetail />} />
        <Route path="household" element={<ResidentMyHousehold />} />
        <Route path="finance" element={<ResidentFinance />} />
        <Route
          path="finance/bills/:id"
          element={<ResidentFinanceDetail kind="bills" />}
        />
        <Route
          path="finance/receipts/:id"
          element={<ResidentFinanceDetail kind="receipts" />}
        />
        <Route path="finance/ledger" element={<ResidentLedger />} />
        <Route path="helpdesk" element={<ResidentHelpdesk />} />
        <Route path="helpdesk/:ticketId" element={<ResidentTicketDetail />} />
        <Route path="technician-services" element={<ResidentTechnicianServices />} />
        <Route path="doorstep-services" element={<ResidentDoorstepServices />} />
        <Route path="notices" element={<ResidentNotices />} />
        <Route path="notices/:noticeId" element={<ResidentNoticeDetail />} />
        <Route path="meetings" element={<ResidentMeetings />} />
        <Route path="meetings/:meetingId" element={<ResidentMeetingDetail />} />
        <Route path="requests" element={<ResidentFormalRequests />} />
        <Route
          path="requests/:requestId"
          element={<ResidentFormalRequestDetail />}
        />
        <Route
          path="visitor-approvals"
          element={<ResidentVisitorApprovals />}
        />
        <Route path="amenities" element={<ResidentAmenities />} />
        <Route path="parking" element={<ResidentParking />} />
        <Route path="documents" element={<ResidentDocuments />} />
        <Route path="profile" element={<ResidentProfile />} />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <SiteHead />
        <BrowserRouter>
          <AppRouter />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </HelmetProvider>
  );
}
